import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Callout,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  ReasonField,
  TextField,
  errorText,
  useCsrf,
  useReason,
  useToast,
} from "@amber/ui";
import {
  copyChangeResponseSchema,
  type CommunicationView,
  type CopyView,
  type EvidenceRecord,
  type MatchProposalView,
} from "../../contracts/index";
import { EVIDENCE_ERROR_FALLBACK, REQUIRED_REASON } from "../format";
import { makePrimaryPreviewQuery, separateCopyPreviewQuery, useEvidenceCommand } from "../queries";
import { rangeLabel } from "./Copies";

export function MakePrimaryDialog({
  record,
  copy,
  onClose,
}: {
  record: EvidenceRecord;
  copy: CopyView;
  onClose: () => void;
}) {
  const toast = useToast();
  const reason = useReason(null, REQUIRED_REASON);
  const csrf = useCsrf();
  const preview = useQuery(makePrimaryPreviewQuery(record.item.id, copy.id, csrf));
  const save = useEvidenceCommand(record.item.id, copyChangeResponseSchema);
  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Make primary…"
      onSubmit={() => {
        if (reason.submit())
          save.mutate(
            {
              path: `/api/evidence/items/${record.item.id}/primary`,
              body: { copy_id: copy.id, reason: reason.trimmed },
            },
            {
              onSuccess: (out) => {
                toast.show(out.message);
                onClose();
              },
            },
          );
      }}
      footer={
        <DialogFooter onClose={onClose} confirm="Make primary" disabled={!preview.data} pending={save.isPending} />
      }
    >
      {preview.error ? (
        <Callout tone="attention" role="alert">
          {errorText(preview.error, EVIDENCE_ERROR_FALLBACK)}
        </Callout>
      ) : null}
      {preview.data ? (
        <>
          <DialogText>
            <b>{preview.data.communication}</b>
          </DialogText>
          <dl className="m-0 grid grid-cols-[110px_1fr] gap-x-3 gap-y-1 text-md">
            <dt className="text-muted">Now primary</dt>
            <dd className="m-0">
              {preview.data.from.title} · {preview.data.from.relationship} · {rangeLabel(preview.data.from.range_ms)}
            </dd>
            <dt className="text-muted">New primary</dt>
            <dd className="m-0 font-strong">
              {preview.data.to.title} · {preview.data.to.relationship} · {rangeLabel(preview.data.to.range_ms)}
            </dd>
          </dl>
          <p className="m-0 mt-2 text-md text-muted">{preview.data.effect}</p>
        </>
      ) : (
        <DialogText>Preparing the preview…</DialogText>
      )}
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <FailedCallout error={save.error} fallback={EVIDENCE_ERROR_FALLBACK} />
    </Dialog>
  );
}

export function SeparateCopyDialog({
  record,
  copy,
  onClose,
}: {
  record: EvidenceRecord;
  copy: CopyView;
  onClose: () => void;
}) {
  const toast = useToast();
  const reason = useReason(null, REQUIRED_REASON);
  const [evidence, setEvidence] = useState("");
  const csrf = useCsrf();
  const preview = useQuery(separateCopyPreviewQuery(record.item.id, copy.id, csrf));
  const save = useEvidenceCommand(record.item.id, copyChangeResponseSchema);
  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Not the same communication…"
      onSubmit={() => {
        if (reason.submit())
          save.mutate(
            {
              path: `/api/evidence/items/${record.item.id}/separate`,
              body: {
                copy_id: copy.id,
                reason: reason.trimmed,
                ...(evidence.trim() ? { evidence: evidence.trim() } : {}),
              },
            },
            {
              onSuccess: (out) => {
                toast.show(out.message);
                onClose();
              },
            },
          );
      }}
      footer={<DialogFooter onClose={onClose} confirm="Separate" disabled={!preview.data} pending={save.isPending} />}
    >
      {preview.error ? (
        <Callout tone="attention" role="alert">
          {errorText(preview.error, EVIDENCE_ERROR_FALLBACK)}
        </Callout>
      ) : null}
      {preview.data ? (
        <>
          <DialogText>
            Separate <b>{preview.data.copy}</b> · {rangeLabel(preview.data.range_ms)}
          </DialogText>
          <ul className="m-0 list-disc pl-5 text-md text-muted">
            {preview.data.readiness.map((r) => (
              <li key={r}>{r}</li>
            ))}
            <li>{preview.data.exports}</li>
            {preview.data.pending_notes > 0 ? (
              <li>
                {preview.data.pending_notes} open note{preview.data.pending_notes === 1 ? "" : "s"} about corrections in
                another copy will close
              </li>
            ) : null}
          </ul>
        </>
      ) : (
        <DialogText>Preparing the preview…</DialogText>
      )}
      <TextField
        label="Evidence"
        optional="optional"
        value={evidence}
        onChange={(e) => {
          setEvidence(e.target.value);
        }}
        placeholder="What shows these are different communications"
      />
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <FailedCallout error={save.error} fallback={EVIDENCE_ERROR_FALLBACK} />
    </Dialog>
  );
}

export function MatchDecisionDialog({
  record,
  communication,
  proposal,
  decision,
  onClose,
}: {
  record: EvidenceRecord;
  communication: CommunicationView;
  proposal: MatchProposalView;
  decision: "same" | "different";
  onClose: () => void;
}) {
  const toast = useToast();
  const reason = useReason(null, REQUIRED_REASON);
  const save = useEvidenceCommand(record.item.id, copyChangeResponseSchema);
  const same = decision === "same";
  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={same ? "Same communication…" : "Different communications…"}
      onSubmit={() => {
        if (reason.submit())
          save.mutate(
            {
              path: `/api/evidence/items/${record.item.id}/matches/decide`,
              body: { copy_id: proposal.id, decision, reason: reason.trimmed },
            },
            {
              onSuccess: (out) => {
                toast.show(out.message);
                onClose();
              },
            },
          );
      }}
      footer={
        <DialogFooter
          onClose={onClose}
          confirm={same ? "Link as one communication" : "Keep apart"}
          pending={save.isPending}
        />
      }
    >
      <DialogText>
        {communication.title ?? `Communication ${String(communication.ordinal)}`} and <b>{proposal.other_title}</b>
      </DialogText>
      <dl className="m-0 grid grid-cols-[130px_1fr] gap-x-3 gap-y-1 text-md">
        <dt className="text-muted">Why it was found</dt>
        <dd className="m-0">{proposal.reason}</dd>
        <dt className="text-muted">Shared wording</dt>
        <dd className="m-0">
          {String(proposal.words)} words · {String(Math.round(proposal.text_score * 100))}% of the passage ·{" "}
          {rangeLabel(proposal.range_ms)}
        </dd>
        <dt className="text-muted">Dates</dt>
        <dd className="m-0">{proposal.dates}</dd>
        <dt className="text-muted">Speakers</dt>
        <dd className="m-0">{proposal.speakers}</dd>
        <dt className="text-muted">Relationship</dt>
        <dd className="m-0">{proposal.relationship_label}</dd>
      </dl>
      <p className="m-0 mt-2 text-md text-muted">
        {same
          ? "Links them as copies of one communication. Each keeps its own transcript, provenance and rights record; readers and exports receive a relationship notice. Not the same communication… reverses it."
          : "Keeps them as separate communications. Copy detection won't suggest this pair again."}
      </p>
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <FailedCallout error={save.error} fallback={EVIDENCE_ERROR_FALLBACK} />
    </Dialog>
  );
}
